import { useState, useEffect } from "react";
import styled from "styled-components";
import BatchService from "../../services/BatchService";

const STATUS_LABELS = {
  CRIADA: "Criada",
  ENVIADA: "Enviada",
  PROCESSANDO: "Processando",
  FINALIZADA: "Finalizada",
  ERRO: "Erro",
};

const STATUS_COLORS = {
  CRIADA: "#6c757d",
  ENVIADA: "#0d6efd",
  PROCESSANDO: "#fd7e14",
  FINALIZADA: "#2fa562",
  ERRO: "#d93025",
};

function ListaArquivosNfe() {
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [errosExpandidos, setErrosExpandidos] = useState({});

  const batchService = new BatchService();

  const carregarArquivos = () => {
    setLoading(true);
    setErro("");
    batchService.getListarArquivosNfe((error, data) => {
      if (error) {
        setErro("Erro ao carregar a lista de arquivos.");
      } else {
        setArquivos(data.arquivos || []);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    carregarArquivos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatarData = (dataStr) => {
    if (!dataStr) return "-";
    const data = new Date(dataStr);
    return data.toLocaleString("pt-BR");
  };

  const toggleErros = (uuid) => {
    setErrosExpandidos((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  };

  return (
    <ListaArquivosStyle>
      <div className="lista-container">
        <div className="lista-header">
          <h2 className="lista-title page-title">Arquivos Enviados</h2>
          <button className="button atualizar-button" onClick={carregarArquivos} disabled={loading}>
            {loading ? "Carregando..." : "Atualizar"}
          </button>
        </div>

        {erro && <p className="status-error">{erro}</p>}

        {!loading && arquivos.length === 0 && !erro && (
          <p className="sem-arquivos">Nenhum arquivo enviado ainda.</p>
        )}

        {arquivos.length > 0 && (
          <div className="tabela-wrapper">
            <table className="tabela-arquivos">
              <thead>
                <tr>
                  <th>Nome do Arquivo</th>
                  <th>Status</th>
                  <th>Data de Envio</th>
                  <th>Data de Processamento</th>
                  <th>Erros</th>
                </tr>
              </thead>
              <tbody>
                {arquivos.map((arquivo) => {
                  const temErros = arquivo.erros && arquivo.erros.trim().length > 0;
                  const expandido = errosExpandidos[arquivo.uuid];

                  return (
                    <>
                      <tr key={arquivo.uuid}>
                        <td>{arquivo.nomeArquivo}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: STATUS_COLORS[arquivo.status] || "#6c757d",
                            }}
                          >
                            {STATUS_LABELS[arquivo.status] || arquivo.status}
                          </span>
                        </td>
                        <td>{formatarData(arquivo.dataCriacao)}</td>
                        <td>{formatarData(arquivo.dataProcessamento)}</td>
                        <td className="erros-cell">
                          {temErros ? (
                            <button
                              className="ver-erros-button"
                              onClick={() => toggleErros(arquivo.uuid)}
                            >
                              {expandido ? "Ocultar erros" : "Ver erros"}
                            </button>
                          ) : (
                            <span className="sem-erro-texto">Nenhum</span>
                          )}
                        </td>
                      </tr>
                      {temErros && expandido && (
                        <tr key={arquivo.uuid + "-erros"} className="erro-detalhe-row">
                          <td colSpan={5}>
                            <div className="erro-detalhe-container">
                              <strong>Erros de importacao:</strong>
                              <pre className="erro-detalhe-texto">{arquivo.erros}</pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ListaArquivosStyle>
  );
}

const ListaArquivosStyle = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  width: 80%;
  border-radius: 10px;
  padding: 30px;
  margin-top: 20px;
  box-sizing: border-box;

  .lista-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .lista-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .lista-title {
    font-size: 3rem;
    color: #340040;
    margin: 0;
    width: fit-content;
  }

  .atualizar-button {
    background: linear-gradient(267deg, rgb(131, 43, 146) 39%, rgb(160, 69, 176) 100%);
    border: none;
    color: white;
    font-weight: 600;
    letter-spacing: 0.2px;
    cursor: pointer;
  }
  .atualizar-button:hover {
    opacity: 0.8;
  }

  .sem-arquivos {
    color: #6c757d;
    text-align: center;
    padding: 20px 0;
    font-size: 1.1rem;
  }

  .status-error {
    color: #d93025;
    font-weight: 500;
  }

  .tabela-wrapper {
    overflow-x: auto;
  }

  .tabela-arquivos {
    width: 100%;
    border-collapse: collapse;
  }

  .tabela-arquivos th,
  .tabela-arquivos td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    font-size: 1.05rem;
  }

  .tabela-arquivos th {
    background-color: #f5f5f5;
    color: #340040;
    font-weight: 700;
    font-size: 1.05rem;
  }

  .tabela-arquivos tbody tr:hover {
    background-color: #faf5fb;
  }

  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    color: white;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .erros-cell {
    text-align: center;
  }

  .sem-erro-texto {
    color: #6c757d;
    font-size: 1.05rem;
  }

  .ver-erros-button {
    background: none;
    border: 1px solid #d93025;
    color: #d93025;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
  }
  .ver-erros-button:hover {
    background-color: #d93025;
    color: white;
  }

  .erro-detalhe-row td {
    padding: 0 12px 12px 12px;
    border-bottom: 2px solid #d93025;
  }

  .erro-detalhe-row:hover {
    background-color: transparent !important;
  }

  .erro-detalhe-container {
    background-color: #fff5f5;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    padding: 12px 16px;
  }

  .erro-detalhe-container strong {
    color: #d93025;
    font-size: 1rem;
    display: block;
    margin-bottom: 6px;
  }

  .erro-detalhe-texto {
    color: #721c24;
    font-size: 1rem;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
    font-family: monospace;
    max-height: 300px;
    overflow-y: auto;
  }
`;

export default ListaArquivosNfe;
